import db from '../../lib/db';
import { currentUserId } from '../../lib/meta';

import type { ChannelId, WorkspaceId } from '../core/types';
import { EchoFeedApi } from './api';
import { getActiveFeedChannelId, getFeedBuffer } from './feedCache';
import { FeedReplicator } from './feedReplicator';
import { createEchoProfileStore } from './profileStore';

interface FeedUserStore {
  get(id: string): Promise<{ readonly whoami?: unknown } | undefined>;
}

interface FeedProfileRowStore {
  get(key: readonly [string, string]): Promise<{ readonly id?: unknown } | undefined>;
}

/**
 * Resolves the viewer's owned profile id in a workspace via the user row's whoami list
 * (same pattern as RouteWorkspaceContext); positive results cached for the resolver's
 * lifetime, misses retried so pre-hydration and freshly-joined workspaces recover.
 */
export function createWorkspaceAccountIdResolver(): (workspaceId: WorkspaceId) => Promise<string | undefined> {
  const cache = new Map<WorkspaceId, string>();
  return async (workspaceId) => {
    const cached = cache.get(workspaceId);
    if (cached) return cached;

    const userId = currentUserId();
    if (!userId) return undefined;
    const stores = db as unknown as { readonly users: FeedUserStore; readonly profiles: FeedProfileRowStore };
    const user = await stores.users.get(String(userId));
    const ownedIds = Array.isArray(user?.whoami) ? user.whoami.map(String) : [];
    if (ownedIds.length === 0) return undefined;

    const candidates = await Promise.all(ownedIds.map((profileId) => stores.profiles.get([workspaceId, profileId])));
    const owned = candidates.find((profile) => profile?.id);
    if (!owned?.id) return undefined;

    const accountId = String(owned.id);
    cache.set(workspaceId, accountId);
    return accountId;
  };
}

let activeReplicator: FeedReplicator | null = null;

/** Resets one catchup-affected channel on the running replicator, if present. */
export function resetFeedReplicatorChannelFromCatchup(workspaceId: WorkspaceId, channelId: ChannelId): void {
  activeReplicator?.resetChannelFromCatchup(workspaceId, channelId);
}

/** Resets all channels on the running replicator before catchup recovery, if present. */
export function resetAllFeedReplicatorChannels(): void {
  activeReplicator?.resetAllChannelsForCatchup();
}

/** Purges a workspace from the feed buffer while fencing any running replication. */
export async function purgeFeedWorkspace(workspaceId: WorkspaceId): Promise<void> {
  if (activeReplicator) {
    await activeReplicator.purgeWorkspace(workspaceId);
    return;
  }
  await getFeedBuffer().purgeWorkspace(workspaceId);
}

/** Starts the page-lifetime feed replicator once; repeat calls are no-ops until stopped. */
export function startFeedReplicator(): void {
  if (activeReplicator) return;
  const profileStore = createEchoProfileStore();
  const replicator = new FeedReplicator({
    buffer: getFeedBuffer(),
    createSyncApi: (channelId: ChannelId) => new EchoFeedApi(channelId, { profileStore }),
    getActiveChannelId: getActiveFeedChannelId,
    resolveAccountId: createWorkspaceAccountIdResolver(),
  });
  replicator.start();
  activeReplicator = replicator;
}

/** Stops and drops the running replicator, if any. */
export function stopFeedReplicator(): void {
  activeReplicator?.stop();
  activeReplicator = null;
}
